import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import "./Billboard.scss";
import { Card, Carousel, Image, Skeleton, Typography } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import DefaultImage from "../../../../assets/default_image_patient.png";
import { getAdvertisements } from "../../../../features/advertisements/advertisementsActions";
import { staticTextErrors } from "../../../../config/constantErrorTexts";

const FALLBACK_SLIDES = [
  {
    image: DefaultImage,
    alt: "advertisement_image",
    title: "Advertisment",
    description:
      "Bold ideas spark connection. Great advertising doesn’t just sell—it inspires, turning simple messages into moments that stay with people long after they’re seen.",
    page_url: "https://blacklist420.com/patients?page=1&per_page=10",
  },
];

export default function Billboard({ token, openNotificationWithIcon }) {
  const dispatch = useDispatch();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const transitionTimerRef = useRef(null);

  useEffect(() => {
    dispatch(getAdvertisements())
      .then((res) => {
        if (res.type === "get_advertisements/fulfilled") {
          if (Array.isArray(res.payload) && res?.payload?.length > 0) {
            const sorted = [...res.payload].sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0)
            );
            setSlides(sorted);
          }else {
            setSlides(FALLBACK_SLIDES);
          }
        } else if (res.type === "get_advertisements/rejected") {
          const error = res?.payload;
          if (typeof error === "object" && error !== null) {
            for (let key in error) {
              if (error[key] && Array.isArray(error[key])) {
                openNotificationWithIcon("error", "Error", error[key]);
                break;
              } else if (
                error[key] &&
                typeof error[key] !== "object"
              ) {
                openNotificationWithIcon("error", "Error", error[key]);
                break;
              }
            }
          } else {
            openNotificationWithIcon(
              "error",
              "Error",
              error && typeof error === "string"
                ? error
                : staticTextErrors?.SERVER_ERROR
            );
          }
          setSlides(FALLBACK_SLIDES);
        }
      })
      .catch(() => {
        setSlides(FALLBACK_SLIDES);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, openNotificationWithIcon]);

  const currentSlide = slides[activeSlide] || slides[0] || {};

  const isSingleSlide = slides.length <= 1;

  useEffect(() => {
    return () => {
      clearTimeout(transitionTimerRef.current);
    };
  }, []);

  const handleSlideChange = useCallback(
    (current) => {
      if (isSingleSlide) return;
      setIsTransitioning(true);
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = setTimeout(() => {
        setActiveSlide(current);
        setIsTransitioning(false);
      }, 250);
    },
    [isSingleSlide]
  );

  return loading ? (
    <div className="billboard">
      <Skeleton.Image active className="billboard__carousel-skeleton" />
      <Card className="billboard__card">
        <Skeleton
          active
          avatar={{ shape: "square", size: 64 }}
          paragraph={{ rows: 2 }}
        />
      </Card>
    </div>
  ) : (
    <div className="billboard">
      <Carousel
        className="billboard__carousel"
        arrows={!isSingleSlide}
        infinite={!isSingleSlide}
        autoplay={!isSingleSlide}
        autoplaySpeed={5000}
        draggable={!isSingleSlide}
        dots={!isSingleSlide}
        adaptiveHeight={true}
        waitForAnimate={true}
        speed={600}
        beforeChange={(_oldIndex, newIndex) => handleSlideChange(newIndex)}>
        {slides?.map((slide) => (
          <div
            key={slide.id || slide.alt}
            className="billboard__carousel__item">
            <img
              src={slide.image || DefaultImage}
              alt={slide.alt || slide?.title || "Advertisement"}
            />
            <div className="billboard__carousel__item__overlay" />
            <div className="billboard__carousel__item__badge">
              {slide?.title || "Sponsored"}
            </div>
          </div>
        ))}
      </Carousel>

      <Card
        className={`billboard__card${isTransitioning ? " billboard__card--transitioning" : ""}`}
        hoverable
        style={{
          backgroundColor: token?.Card?.colorBgCard,
        }}
        onClick={() => {
          if (currentSlide.page_url && /^https?:\/\//.test(currentSlide.page_url)) {
            window.open(currentSlide.page_url, "_blank", "noopener,noreferrer");
          }
        }}>
        <div
          key={activeSlide}
          className={`billboard__card__body${isTransitioning ? " billboard__card__body--transitioning" : ""}`}>
          <div
            className="billboard__card__imgWrap"
            onClick={(e) => {
              e.stopPropagation();
            }}>
            <Image
              src={currentSlide.image || DefaultImage}
              alt={currentSlide.alt || currentSlide?.title || "Advertisement"}
              width="64px"
              height="64px"
              style={{ borderRadius: "8px" }}
            />
          </div>
          <div className="billboard__card__descriptionCont">
            <Typography.Text strong>{currentSlide.title}</Typography.Text>
            <Typography.Text>{currentSlide.description}</Typography.Text>
            <Typography.Link
              href={currentSlide.page_url}
              target="_blank"
              rel="noopener noreferrer">
              Learn More {""}
              <span className="billboard__card__arrow">
                <ArrowRightOutlined
                  style={{
                    fontSize: "14px",
                  }}
                />
              </span>
            </Typography.Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
